<template>
  <!-- Outer wrapper -->
  <div class="relative">

    <!-- Background vectors -->
    <div class="absolute inset-0 pointer-events-none overflow-hidden -z-10">
      <div
        class="absolute top-16 left-10 md:left-[40px] w-64 h-64 bg-no-repeat bg-contain opacity-20"
        :style="`background-image: url(${art})`"
      ></div>

      <div
        class="absolute bottom-20 right-[-40px] w-72 h-72 bg-no-repeat bg-contain opacity-20"
        :style="`background-image: url(${art2})`"
      ></div>
    </div>

    <!-- Tab Selector -->
    <div class="flex space-x-4 mb-6 justify-center">
      <button
        @click="setActiveType('newspaper')"
        :class="activeType === 'newspaper' ? 'bg-[#D84040] text-white' : 'bg-gray-200 hover:bg-gray-300'"
        class="px-4 py-2 rounded font-semibold"
      >
        Newspapers
      </button>

      <button
        @click="setActiveType('magazine')"
        :class="activeType === 'magazine' ? 'bg-[#D84040] text-white' : 'bg-gray-200 hover:bg-gray-300'"
        class="px-4 py-2 rounded font-semibold"
      >
        Magazines
      </button>
    </div>

    <!-- Main content -->
    <main class="relative z-10 w-full h-full space-y-6 px-6 md:px-0">

  <!-- Error -->
  <div v-if="error" class="text-center text-red-600 font-medium">
    {{ error }}
  </div>

  <!-- No Issues -->
  <section
    v-else-if="paginatedIssues.length === 0"
    class="bg-white p-10 shadow-md rounded-md"
  >
    <p class="text-gray-600 text-center">
      No {{ activeType }} available.
    </p>
  </section>

  <!-- Issues Exist -->
  <template v-else>

    <!-- 🔔 Payment Notice -->
    <div class="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-md text-sm">
      <p v-if="activeType === 'newspaper'">
        📰 <strong>Note:</strong> To download the latest month's newspaper, a payment of <strong>₹20</strong> is required.
      </p>

      <p v-else>
        📖 <strong>Note:</strong> To download any magazine, a payment of <strong>₹100</strong> is required.
      </p>
    </div>

    <!-- 📰 Issues List -->
    <section class="bg-white py-16 shadow-md rounded-md space-y-6 h-full flex flex-col">
      <div v-for="issue in paginatedIssues" :key="issue.id" class="issue-card text-center pb-3 h-full flex flex-col">
        <img
          :src="getFullUrl(issue.thumbnail_path)"
          :alt="issue.title"
          class="w-full h-64 md:h-[350px] object-contain rounded-lg mb-4 shadow-sm border border-gray-100 bg-gray-50/50"
        />
        <h2 class="text-2xl font-bold mb-3 text-gray-800">
          {{ issue.title }}
        </h2>
        <p class="text-gray-600 text-[15px] mb-auto">
          {{ issue.description }}
        </p>
        <div class="mt-6">
          <button
            @click="handleDownload(issue)"
            class="inline-flex justify-center items-center gap-2 px-8 py-3 rounded-full transition font-semibold shadow-sm hover:shadow-md hover:-translate-y-0.5"
            :class="activeType === 'newspaper' && issue.is_paid
              ? 'bg-[#D84040] text-white hover:bg-[#b73232]'
              : 'bg-[#FFE9A0] text-gray-900 hover:bg-[#FFD580]'"
          >
            <span v-if="activeType === 'newspaper' && issue.is_paid">Buy to Download</span>
            <span v-else>⬇️ Download PDF</span>
          </button>
        </div>
        <hr
          class="my-6 border-gray-200 hidden"
          v-if="issue !== paginatedIssues[paginatedIssues.length - 1]"
        />
      </div>
    </section>

  </template>

  <!-- Pagination -->
  <div v-if="totalPages > 1" class="flex justify-center mt-6 space-x-2">
    <button
      @click="prevPage"
      :disabled="currentPage === 1"
      class="pagination-btn px-3 py-1 bg-gray-300 text-sm rounded hover:bg-gray-400 disabled:opacity-50"
    >
      Previous
    </button>

    <button
      v-for="(page, index) in paginationPages"
      :key="index"
      @click="page !== '...' && goToPage(page)"
      :disabled="page === '...'"
      :class="[
        'pagination-btn px-3 py-1 rounded text-sm transition-colors',
        page === '...' ? 'bg-transparent text-gray-500 cursor-default' : 
        currentPage === page
          ? 'bg-[#D84040] text-white font-bold shadow-sm'
          : 'bg-gray-200 hover:bg-gray-300 text-gray-700',
      ]"
    >
      {{ page }}
    </button>

    <button
      @click="nextPage"
      :disabled="currentPage === totalPages"
      class="pagination-btn px-3 py-1 bg-gray-300 text-sm rounded hover:bg-gray-400 disabled:opacity-50"
    >
      Next
    </button>
  </div>

</main>
  </div>
</template>

<script setup>
import art from '@/assets/images/art.png'
import Swal from 'sweetalert2'
import art2 from '@/assets/images/art22.png'
import { ref, computed, onMounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

// State
const activeType = ref('newspaper') // 'newspaper' or 'magazine'
const issues = ref([])
const error = ref('')
const currentPage = ref(1)
const itemsPerPage = 1 // Show 1 PDF at a time

// API Base URLs
const API_BASE_URL = process.env.VUE_APP_API_BASE_URL
const FILE_BASE_URL = process.env.VUE_APP_FILE_BASE_URL

// Computed for pagination
const totalPages = computed(() =>
  Math.ceil(issues.value.length / itemsPerPage)
)

const paginationPages = computed(() => {
  const current = currentPage.value;
  const total = totalPages.value;
  if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 3) return [1, 2, 3, 4, '...', total];
  if (current >= total - 2) return [1, '...', total - 3, total - 2, total - 1, total];
  return [1, '...', current - 1, current, current + 1, '...', total];
});

const paginatedIssues = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage
  return issues.value.slice(start, start + itemsPerPage)
})

// Animations
const animateItems = () => {
  nextTick(() => {
    gsap.from('.issue-card', {
      scrollTrigger: {
        trigger: '.issue-card',
        start: 'top 85%',
        toggleActions: 'play none none none',
      },
      y: 50,
      opacity: 0,
      duration: 0.8,
      stagger: 0.2,
      ease: 'power2.out',
    })

    gsap.from('.pagination-btn', {
      opacity: 0,
      scale: 0.95,
      duration: 0.6,
      stagger: 0.1,
      ease: 'back.out(1.7)',
    })
  })
}

// Fetch data
const fetchIssues = async () => {
  try {
    error.value = ''
    const response = await axios.get(`${API_BASE_URL}/${activeType.value}s`)
    issues.value = response.data
    currentPage.value = 1
    animateItems()
  } catch (err) {
    console.error(`❌ Error fetching ${activeType.value}s:`, err)
    error.value = `Failed to load ${activeType.value}s. Please try again later.`
  }
}

// Helpers
const getFullUrl = (path) => {
  if (!path) return '#'
  if (path.startsWith('http')) return path
  const base = FILE_BASE_URL?.replace(/\/$/, '') ?? ''
  return `${base}${path.startsWith('/') ? path : `/${path}`}`
}
const getPdfUrl = (path) => getFullUrl(path)

// Pagination
const goToPage = (page) => { currentPage.value = page }
const prevPage = () => { if (currentPage.value > 1) currentPage.value-- }
const nextPage = () => { if (currentPage.value < totalPages.value) currentPage.value++ }

// Switch tab
const setActiveType = (type) => {
  if (activeType.value === type) return
  activeType.value = type
  fetchIssues()
}

// Handle Download — checks login + paid access before downloading
const router = useRouter()
const handleDownload = async (issue) => {
  const token = localStorage.getItem('token')

  // For newspapers only: check is_paid flag
  if (activeType.value === 'newspaper' && issue.is_paid) {
    // Must be logged in first
    if (!token) {
      Swal.fire({
        icon: 'warning',
        title: 'Login Required',
        text: 'Please log in to purchase and download this newspaper.',
        confirmButtonColor: '#BF3131',
        showCancelButton: true,
        confirmButtonText: 'Go to Login'
      }).then(r => { if (r.isConfirmed) router.push('/login') })
      return
    }

    // Check if user has already purchased this newspaper
    try {
      const res = await axios.get(`${API_BASE_URL}/newspapers/${issue.id}/access`, {
        headers: { Authorization: `Bearer ${token}` }
      })

      if (res.data.has_access) {
        // ✅ Paid & access confirmed — trigger download
        triggerDownload(getPdfUrl(issue.pdf_path))
      } else {
        // ❌ Not purchased yet — show price & redirect to subscribe
        const result = await Swal.fire({
          icon: 'info',
          title: 'Paid Issue',
          html: `To download this newspaper, a payment of <b>₹20</b> is required.<br><br>Subscribe to get access.`,
          confirmButtonColor: '#BF3131',
          confirmButtonText: 'Subscribe Now',
          showCancelButton: true,
          cancelButtonText: 'Cancel'
        })
        if (result.isConfirmed) router.push('/subscribe')
      }
    } catch (err) {
      console.error('Access check failed:', err)
      Swal.fire({ icon: 'error', title: 'Error', text: 'Could not verify access. Try again.', confirmButtonColor: '#BF3131' })
    }
    return
  }

  // Free issue or magazine — just check login
  if (!token) {
    Swal.fire({
      icon: 'warning',
      title: 'Login Required',
      text: 'You must be logged in to download.',
      confirmButtonColor: '#BF3131'
    })
    router.push('/login')
    return
  }

  triggerDownload(getPdfUrl(issue.pdf_path))
}

// Actual browser download trigger
const triggerDownload = (url) => {
  const link = document.createElement('a')
  link.href = url
  link.target = '_blank'
  link.download = ''
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

// Initial load
onMounted(fetchIssues)
</script>
